/**
 * Per-chunk generation context shared by the worldgen passes (terrain, ores,
 * features, structures). Column data covers the chunk plus a MARGIN-wide
 * border so features rooted in a neighbour can spill in deterministically.
 */

import { CHUNK_SIZE, SEA_LEVEL, WORLD_HEIGHT } from '../constants';
import type { TerrainGenerator } from './terrain';

/** Border (blocks) around the chunk for which column data is known. */
export const MARGIN = 8;
/** Side length of the padded column grid. */
export const PSIZE = CHUNK_SIZE + MARGIN * 2;

/** Vanilla Y -> this world's Y (half vertical scale around sea level). */
export const vy = (v: number): number => Math.round(SEA_LEVEL + (v - 63) * 0.5);

export interface GenCtx {
  /** For climate / biome queries outside the padded grid. */
  gen: TerrainGenerator;
  seed: number;
  cx: number;
  cz: number;
  /** World coordinates of the chunk's (0, 0) column. */
  ox: number;
  oz: number;
  out: Uint16Array;
  /** Padded column grids, index `pcol(ctx, wx, wz)`. */
  top: Int16Array;      // highest terrain block (before caves and features)
  biome: Uint8Array;
  tempIdx: Uint8Array;  // vanilla temperature band 0 (frozen) .. 4 (hot)
  humid: Float32Array;
  cont: Float32Array;
  erosion: Float32Array;
}

export function pcol(ctx: GenCtx, wx: number, wz: number): number {
  return wx - ctx.ox + MARGIN + (wz - ctx.oz + MARGIN) * PSIZE;
}

/** Is the column inside the padded grid? */
export function known(ctx: GenCtx, wx: number, wz: number): boolean {
  const x = wx - ctx.ox + MARGIN;
  const z = wz - ctx.oz + MARGIN;
  return x >= 0 && x < PSIZE && z >= 0 && z < PSIZE;
}

/** Block id at a world position, or -1 outside this chunk. */
export function getBlock(ctx: GenCtx, wx: number, wy: number, wz: number): number {
  const x = wx - ctx.ox;
  const z = wz - ctx.oz;
  if (x < 0 || x >= CHUNK_SIZE || z < 0 || z >= CHUNK_SIZE || wy < 0 || wy >= WORLD_HEIGHT) return -1;
  return ctx.out[x + z * CHUNK_SIZE + wy * 256];
}

/** Write a block; positions outside this chunk are silently dropped. */
export function setBlock(ctx: GenCtx, wx: number, wy: number, wz: number, id: number): void {
  const x = wx - ctx.ox;
  const z = wz - ctx.oz;
  if (x < 0 || x >= CHUNK_SIZE || z < 0 || z >= CHUNK_SIZE || wy < 1 || wy >= WORLD_HEIGHT) return;
  ctx.out[x + z * CHUNK_SIZE + wy * 256] = id;
}
